import { Grid, Typography } from "@material-ui/core";
import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import FormDropdown from "../../../components/molecules/form-dropdown";
import PreferenceToggle from "./preference-toggle";
import MailingSection from "./mailing-section";
import FormMeta from "./form-meta";
import OfflineReponsesDomain from "../../../features/offline-responses";
import { OFFLINE_RESPONSES_OPTIONS } from "../../../static-data";
import AppLogger from "../../../utils/logger";
import { setAppSnackNotification } from "../../../store/actions/application";
import PageSection from "../page-section";
import { getAuthUserAccount } from "../../../features/application";
import { validationSchema } from "./validation-schema";
import { OFFLINE_REQUEST_TYPES, PRICING_TIERS } from "../../../config/constant";
import ActionFooter from "../action-footer";
import AppDialog, {
  DialogContent,
  DialogFooter,
  PrimaryAction,
  TextButton,
} from "../../../components/molecules/dialog";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import Notice, { Varaint } from "../../../components/molecules/notice";
import "../style.css";

const OfflineResponses = () => {
  const { authAccount, authProfile } = useSelector(
    (state) => state.application
  );
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const [isSubmittingData, setIsSubmittingData] = useState(false);

  const labelStyles = {
    textAlign: "left",
    width: "60%",
    fontSize: 20,
  };

  const onCloseEmailModal = ({ setFieldValue, initialValues }) => {
    setFieldValue(
      FormMeta.offlineRequestType.fieldName,
      initialValues[FormMeta.offlineRequestType.fieldName]
    );
    setDisplayEmailModal(false);
  };

  const onClickAddEmail = () => {
    history.push({ pathname: "/account", hash: "update-email-input" });
  };

  const initialValues = useMemo(() => {
    return OfflineReponsesDomain.bindInitialFormValues({ authAccount });
  }, [authAccount]);

  const handleSubmit = async ({ values, initialValues }) => {
    if (
      OfflineReponsesDomain.emailInputModalVisivility({ authAccount, values })
    ) {
      setDisplayEmailModal(true);
      return;
    }

    const hasChangedAddressFields = OfflineReponsesDomain.hasChangedAddress({
      initialValues,
      values,
    });
    const hasChangedPreferences = OfflineReponsesDomain.hasChangedPreferences({
      initialValues,
      values,
    });

    const shouldUpdateAddress =
      (values.showOfflineId &&
        values.offlineRequestType === OFFLINE_REQUEST_TYPES.POST) ||
      values.subscribeMagazine;

    try {
      setIsSubmittingData(true);
      if (hasChangedAddressFields && shouldUpdateAddress) {
        const addressFields = OfflineReponsesDomain.getUpdatedAddressValues({
          values,
        });
        const response = await OfflineReponsesDomain.updateAddress({
          addressFields,
        });

        if (response.success) {
          dispatch(
            setAppSnackNotification({
              severity: "success",
              message: t("common.updatedsuccessfully"),
            })
          );
        }
      }

      if (hasChangedPreferences) {
        const preferences = OfflineReponsesDomain.getUpdatedPreferences({
          values,
        });
        const response = await OfflineReponsesDomain.updatePreferences({
          preferences,
        });
        if (response.success) {
          dispatch(
            setAppSnackNotification({
              severity: "success",
              message: t("common.updatedsuccessfully"),
            })
          );
        }
      }

      if (
        (hasChangedAddressFields && shouldUpdateAddress) ||
        hasChangedPreferences
      ) {
        dispatch(getAuthUserAccount());
      }
    } catch (error) {
      dispatch(
        setAppSnackNotification({
          severity: "error",
          message: t("errors.errorOccured"),
        })
      );
      AppLogger.exception(error);
      console.log("Error >>>", error);
    } finally {
      setIsSubmittingData(false);
    }
  };

  const disableSubscribeMagazine = useMemo(() => {
    return authProfile.pricingTier?.id === PRICING_TIERS["2M_BASIC"];
  }, [authProfile]);

  //Diable subscribeMagazine when user selects 2m_basic plan
  useEffect(() => {
    const shouldDisableSubscribeMagazine =
      authProfile.pricingTier?.id === PRICING_TIERS["2M_BASIC"] &&
      authAccount.userPreferences?.subscribeMagazine;

    if (shouldDisableSubscribeMagazine) {
      OfflineReponsesDomain.updatePreferences({
        preferences: { subscribeMagazine: false },
      }).then(() => {
        dispatch(getAuthUserAccount());
      });
    }
  }, [authAccount, authProfile, dispatch]);

  return (
    <PageSection
      id="offline-responses-div"
      heading={t("createflow.offlineResponses")}
      subTitle={t("createflow.offlineResponsesSubheading")}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema({ t })}
        enableReinitialize
        onSubmit={(values, actions) =>
          handleSubmit({ values, actions, initialValues })
        }
      >
        {({
          values,
          errors,
          handleChange,
          dirty,
          resetForm,
          submitCount,
          handleSubmit,
          setFieldValue,
        }) => (
          <Grid container direction="column" className="row-gap-24">
            <PreferenceToggle
              id={FormMeta.showOfflineId.fieldName}
              label={t(FormMeta.showOfflineId.label)}
              value={values[FormMeta.showOfflineId.fieldName]}
              handleChange={handleChange}
            />

            {values[FormMeta.showOfflineId.fieldName] && (
              <Grid container xs={12} sm={6}>
                <FormDropdown
                  fieldName={FormMeta.offlineRequestType.fieldName}
                  label={t(FormMeta.offlineRequestType.label)}
                  labelStyles={labelStyles}
                  options={OFFLINE_RESPONSES_OPTIONS}
                  keyExtractor={FormMeta.offlineRequestType.keyExtractor}
                  labelExtractor={FormMeta.offlineRequestType.labelExtractor}
                  lang={i18n.language}
                  value={values[FormMeta.offlineRequestType.fieldName]}
                  errorText={
                    submitCount > 0 &&
                    errors[FormMeta.offlineRequestType.fieldName]
                  }
                  alignFormItems="flex-start"
                  onChange={handleChange}
                />
              </Grid>
            )}

            <PreferenceToggle
              id={FormMeta.subscribeMagazine.fieldName}
              label={t(FormMeta.subscribeMagazine.label)}
              value={values[FormMeta.subscribeMagazine.fieldName]}
              handleChange={handleChange}
              disabled={disableSubscribeMagazine}
            />

            {disableSubscribeMagazine && (
              <Notice
                varaint={Varaint.primary}
                label={t("settingPage.monthlyMagazineAllowedPackagesNotice")}
                lang={i18n.language}
                customClass="subscribe-magazine-notice"
              />
            )}

            <MailingSection />

            <ActionFooter
              onPrimaryAction={handleSubmit}
              onSecondaryAction={() => resetForm()}
              disablePrimary={!dirty || isSubmittingData}
              disableSecondary={!dirty || isSubmittingData}
              isLoading={isSubmittingData}
            />

            <AppDialog
              showModal={displayEmailModal}
              title={t("settingPage.addYourEmailToContinue")}
              initialized={true}
              onCloseModal={() =>
                onCloseEmailModal({ setFieldValue, initialValues })
              }
            >
              <DialogContent paddingY={8}>
                <Typography variant="body2">
                  {t("settingPage.missingEmailText")}
                </Typography>
                <Typography variant="body2">
                  {t("settingPage.emailAddInstruction")}
                </Typography>
              </DialogContent>
              <DialogFooter>
                <PrimaryAction
                  label={t("settingPage.addEmail")}
                  fullWidth
                  onClick={() => onClickAddEmail()}
                />
                <TextButton
                  label={t("common.cancel")}
                  fullWidth
                  onClick={() =>
                    onCloseEmailModal({ setFieldValue, initialValues })
                  }
                />
              </DialogFooter>
            </AppDialog>
          </Grid>
        )}
      </Formik>
    </PageSection>
  );
};

export default OfflineResponses;
